export type PlanType = 'free' | 'pro';

export type SubscriptionStatus = 'active' | 'cancelled' | 'past_due' | 'paused' | 'none';

export type PaymentProvider = 'stripe' | 'paddle';

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled';

export type QuotationStatus = 'draft' | 'sent' | 'accepted' | 'declined' | 'expired';

export type TaxDiscountType = 'percentage' | 'fixed' | 'none' | 'vat' | 'sst';

export type Currency = 'MYR' | 'USD' | 'SGD' | 'EUR' | 'GBP' | 'AUD' | 'JPY' | 'CNY';

export type TemplateId = 'default' | 'modern' | 'classic' | 'minimal' | 'bold' | 'elegant';

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string | null;
  plan: PlanType;
  isBlocked?: boolean;
  downloadCount: number;
  lastDownloadDate?: string;
  dailyDownloadCount?: number;
  mfaEnabled?: boolean;
  predefinedTaxes?: Tax[];
  defaultCurrency?: Currency;
  createdAt: Date;
  subscription: {
    status: SubscriptionStatus;
    provider?: PaymentProvider;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    paddleCustomerId?: string;
    paddleSubscriptionId?: string;
    currentPeriodEnd?: Date;
    scheduledCancellation?: boolean;
    cancelAt?: Date;
  };
}

export interface Business {
  businessId: string;
  name: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  logoUrl: string | null;
  bankDetails: string;
  taxNumber: string;
  isDefault: boolean;
  createdAt: Date;
  stripePaymentLink?: string;
  paypalLink?: string;
  disableAutoApplyPaymentLinks?: boolean;
}

export interface Customer {
  customerId: string;
  name: string;
  companyName: string;
  address: string;
  email: string;
  phone: string;
  taxNumber: string;
  isDefault: boolean;
  createdAt: Date;
}

export interface InventoryItem {
  itemId: string;
  code: string;
  name: string;
  description: string;
  rate: number | string;
  unit: string;
  createdAt: Date;
}


export interface Tax {
  label: string;
  type: TaxDiscountType;
  value: number | string;
  enabled: boolean;
}

export interface Discount {
  enabled: boolean;
  type: TaxDiscountType;
  value: number | string;
  label?: string;
}

export interface LineItemTax {
  enabled: boolean;
  type: TaxDiscountType;
  label: string;
  value: number | string;
}

export interface LineItemDiscount {
  enabled: boolean;
  type: TaxDiscountType;
  value: number | string;
}

export interface LineItem {
  id?: string;
  description: string;
  quantity: number | string;
  unitPrice?: number | string;
  unit?: string;
  lineTax?: LineItemTax;
  lineDiscount?: LineItemDiscount;
  subtotal?: number;
  rate?: number | string;
  amount?: number;
}

export interface BillTo {
  name: string;
  companyName?: string;
  address: string;
  email: string;
  phone: string;
  taxNumber: string;
}

export interface InvoiceSettings {
  headerTax: {
    enabled: boolean;
    taxes: Tax[];
  };
  headerDiscount: Discount;
  customTaxes: Tax[];
  showLineTax: boolean;
  showLineDiscount: boolean;
  applyTaxOn: 'subtotal' | 'discounted';
}

export interface Invoice {
  invoiceId: string;
  businessId: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  issueDate: Date;
  dueDate: Date;
  currency: Currency;
  poNumber?: string;
  billTo: BillTo;
  lineItems: LineItem[];
  settings: InvoiceSettings;
  notes: string;
  terms: string;
  footer?: string;
  templateId: TemplateId;
  templateColor?: string;
  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  totalAmount: number;
  createdAt: Date;
  updatedAt: Date;
  enablePaymentLinks?: boolean;
}

export interface InvoiceFormData extends Omit<Invoice, 'invoiceId' | 'createdAt' | 'updatedAt' | 'subtotal' | 'totalDiscount' | 'totalTax' | 'totalAmount'> { }

export interface Quotation {
  quotationId: string;
  businessId: string;
  quotationNumber: string;
  status: QuotationStatus;
  issueDate: Date;
  validUntil: Date;
  currency: Currency;
  poNumber?: string;
  billTo: BillTo;
  lineItems: LineItem[];
  settings: InvoiceSettings;
  notes: string;
  terms: string;
  footer?: string;
  templateId: TemplateId;
  templateColor?: string;
  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  totalAmount: number;
  createdAt: Date;
  updatedAt: Date;
  enablePaymentLinks?: boolean;
}

export interface QuotationFormData extends Omit<Quotation, 'quotationId' | 'createdAt' | 'updatedAt' | 'subtotal' | 'totalDiscount' | 'totalTax' | 'totalAmount'> { }

export const CURRENCIES: { code: Currency; symbol: string; name: string }[] = [
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
];

export const ALL_CURRENCIES: { code: string; symbol: string; name: string }[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'ARS', symbol: '$', name: 'Argentine Peso' },
  { code: 'AFN', symbol: '؋', name: 'Afghan Afghani' },
  { code: 'ALL', symbol: 'L', name: 'Albanian Lek' },
  { code: 'AMD', symbol: '֏', name: 'Armenian Dram' },
  { code: 'ANG', symbol: 'ƒ', name: 'Netherlands Antillean Guilder' },
  { code: 'AOA', symbol: 'Kz', name: 'Angolan Kwanza' },
  { code: 'AWG', symbol: 'ƒ', name: 'Aruban Florin' },
  { code: 'AZN', symbol: '₼', name: 'Azerbaijani Manat' },
  { code: 'BAM', symbol: 'KM', name: 'Bosnia-Herzegovina Convertible Mark' },
  { code: 'BBD', symbol: '$', name: 'Barbadian Dollar' },
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka' },
  { code: 'BGN', symbol: 'лв', name: 'Bulgarian Lev' },
  { code: 'BHD', symbol: '.د.ب', name: 'Bahraini Dinar' },
  { code: 'BIF', symbol: 'Fr', name: 'Burundian Franc' },
  { code: 'BMD', symbol: '$', name: 'Bermudan Dollar' },
  { code: 'BND', symbol: '$', name: 'Brunei Dollar' },
  { code: 'BOB', symbol: 'Bs.', name: 'Bolivian Boliviano' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'BSD', symbol: '$', name: 'Bahamian Dollar' },
  { code: 'BTN', symbol: 'Nu.', name: 'Bhutanese Ngultrum' },
  { code: 'BWP', symbol: 'P', name: 'Botswanan Pula' },
  { code: 'BYN', symbol: 'Br', name: 'Belarusian Ruble' },
  { code: 'BZD', symbol: '$', name: 'Belize Dollar' },
  { code: 'CDF', symbol: 'Fr', name: 'Congolese Franc' },
  { code: 'CLP', symbol: '$', name: 'Chilean Peso' },
  { code: 'COP', symbol: '$', name: 'Colombian Peso' },
  { code: 'CRC', symbol: '₡', name: 'Costa Rican Colón' },
  { code: 'CUP', symbol: '$', name: 'Cuban Peso' },
  { code: 'CVE', symbol: '$', name: 'Cape Verdean Escudo' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
  { code: 'DJF', symbol: 'Fr', name: 'Djiboutian Franc' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'DOP', symbol: '$', name: 'Dominican Peso' },
  { code: 'DZD', symbol: 'د.ج', name: 'Algerian Dinar' },
  { code: 'EGP', symbol: '£', name: 'Egyptian Pound' },
  { code: 'ERN', symbol: 'Nfk', name: 'Eritrean Nakfa' },
  { code: 'ETB', symbol: 'Br', name: 'Ethiopian Birr' },
  { code: 'FJD', symbol: '$', name: 'Fijian Dollar' },
  { code: 'FKP', symbol: '£', name: 'Falkland Islands Pound' },
  { code: 'FOK', symbol: 'kr', name: 'Faroese Króna' },
  { code: 'GEL', symbol: '₾', name: 'Georgian Lari' },
  { code: 'GGP', symbol: '£', name: 'Guernsey Pound' },
  { code: 'GHS', symbol: '₵', name: 'Ghanaian Cedi' },
  { code: 'GIP', symbol: '£', name: 'Gibraltar Pound' },
  { code: 'GMD', symbol: 'D', name: 'Gambian Dalasi' },
  { code: 'GNF', symbol: 'Fr', name: 'Guinean Franc' },
  { code: 'GTQ', symbol: 'Q', name: 'Guatemalan Quetzal' },
  { code: 'GYD', symbol: '$', name: 'Guyanaese Dollar' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'HNL', symbol: 'L', name: 'Honduran Lempira' },
  { code: 'HRK', symbol: 'kn', name: 'Croatian Kuna' },
  { code: 'HTG', symbol: 'G', name: 'Haitian Gourde' },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
  { code: 'ILS', symbol: '₪', name: 'Israeli New Shekel' },
  { code: 'IMP', symbol: '£', name: 'Isle of Man Pound' },
  { code: 'IQD', symbol: 'ع.د', name: 'Iraqi Dinar' },
  { code: 'IRR', symbol: '﷼', name: 'Iranian Rial' },
  { code: 'ISK', symbol: 'kr', name: 'Icelandic Króna' },
  { code: 'JEP', symbol: '£', name: 'Jersey Pound' },
  { code: 'JMD', symbol: '$', name: 'Jamaican Dollar' },
  { code: 'JOD', symbol: 'د.ا', name: 'Jordanian Dinar' },
  { code: 'KES', symbol: 'Sh', name: 'Kenyan Shilling' },
  { code: 'KGS', symbol: 'с', name: 'Kyrgystani Som' },
  { code: 'KHR', symbol: '៛', name: 'Cambodian Riel' },
  { code: 'KID', symbol: '$', name: 'Kiribati Dollar' },
  { code: 'KMF', symbol: 'Fr', name: 'Comorian Franc' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar' },
  { code: 'KYD', symbol: '$', name: 'Cayman Islands Dollar' },
  { code: 'KZT', symbol: '₸', name: 'Kazakhstani Tenge' },
  { code: 'LAK', symbol: '₭', name: 'Laotian Kip' },
  { code: 'LBP', symbol: 'ل.ل', name: 'Lebanese Pound' },
  { code: 'LKR', symbol: 'Rs', name: 'Sri Lankan Rupee' },
  { code: 'LRD', symbol: '$', name: 'Liberian Dollar' },
  { code: 'LSL', symbol: 'L', name: 'Lesotho Loti' },
  { code: 'LYD', symbol: 'ل.د', name: 'Libyan Dinar' },
  { code: 'MAD', symbol: 'د.م.', name: 'Moroccan Dirham' },
  { code: 'MDL', symbol: 'L', name: 'Moldovan Leu' },
  { code: 'MGA', symbol: 'Ar', name: 'Malagasy Ariary' },
  { code: 'MKD', symbol: 'ден', name: 'Macedonian Denar' },
  { code: 'MMK', symbol: 'Ks', name: 'Myanmar Kyat' },
  { code: 'MNT', symbol: '₮', name: 'Mongolian Tugrik' },
  { code: 'MOP', symbol: 'P', name: 'Macanese Pataca' },
  { code: 'MRU', symbol: 'UM', name: 'Mauritanian Ouguiya' },
  { code: 'MUR', symbol: '₨', name: 'Mauritian Rupee' },
  { code: 'MVR', symbol: 'ރ.', name: 'Maldivian Rufiyaa' },
  { code: 'MWK', symbol: 'MK', name: 'Malawian Kwacha' },
  { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
  { code: 'MZN', symbol: 'MT', name: 'Mozambican Metical' },
  { code: 'NAD', symbol: '$', name: 'Namibian Dollar' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'NIO', symbol: 'C$', name: 'Nicaraguan Córdoba' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'NPR', symbol: '₨', name: 'Nepalese Rupee' },
  { code: 'OMR', symbol: 'ر.ع.', name: 'Omani Rial' },
  { code: 'PAB', symbol: 'B/.', name: 'Panamanian Balboa' },
  { code: 'PEN', symbol: 'S/.', name: 'Peruvian Sol' },
  { code: 'PGK', symbol: 'K', name: 'Papua New Guinean Kina' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty' },
  { code: 'PYG', symbol: '₲', name: 'Paraguayan Guarani' },
  { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Rial' },
  { code: 'RON', symbol: 'lei', name: 'Romanian Leu' },
  { code: 'RSD', symbol: 'дин.', name: 'Serbian Dinar' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
  { code: 'RWF', symbol: 'Fr', name: 'Rwandan Franc' },
  { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal' },
  { code: 'SBD', symbol: '$', name: 'Solomon Islands Dollar' },
  { code: 'SCR', symbol: '₨', name: 'Seychellois Rupee' },
  { code: 'SDG', symbol: 'ج.س.', name: 'Sudanese Pound' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krone' },
  { code: 'SHP', symbol: '£', name: 'Saint Helena Pound' },
  { code: 'SLE', symbol: 'Le', name: 'Sierra Leonean Leone' },
  { code: 'SOS', symbol: 'Sh', name: 'Somali Shilling' },
  { code: 'SRD', symbol: '$', name: 'Surinamese Dollar' },
  { code: 'SSP', symbol: '£', name: 'South Sudanese Pound' },
  { code: 'STN', symbol: 'Db', name: 'São Tomé and Príncipe Dobra' },
  { code: 'SYP', symbol: '£', name: 'Syrian Pound' },
  { code: 'SZL', symbol: 'L', name: 'Swazi Lilangeni' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  { code: 'TJS', symbol: 'ЅМ', name: 'Tajikistani Somoni' },
  { code: 'TMT', symbol: 'm', name: 'Turkmenistani Manat' },
  { code: 'TND', symbol: 'د.ت', name: 'Tunisian Dinar' },
  { code: 'TOP', symbol: 'T$', name: 'Tongan Paʻanga' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  { code: 'TTD', symbol: '$', name: 'Trinidad and Tobago Dollar' },
  { code: 'TVD', symbol: '$', name: 'Tuvaluan Dollar' },
  { code: 'TWD', symbol: 'NT$', name: 'New Taiwan Dollar' },
  { code: 'TZS', symbol: 'Sh', name: 'Tanzanian Shilling' },
  { code: 'UAH', symbol: '₴', name: 'Ukrainian Hryvnia' },
  { code: 'UGX', symbol: 'Sh', name: 'Ugandan Shilling' },
  { code: 'UYU', symbol: '$', name: 'Uruguayan Peso' },
  { code: 'UZS', symbol: "so'm", name: 'Uzbekistan Som' },
  { code: 'VES', symbol: 'Bs.', name: 'Venezuelan Bolívar' },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong' },
  { code: 'VUV', symbol: 'Vt', name: 'Vanuatu Vatu' },
  { code: 'WST', symbol: 'T', name: 'Samoan Tala' },
  { code: 'XAF', symbol: 'Fr', name: 'Central African CFA Franc' },
  { code: 'XCD', symbol: '$', name: 'East Caribbean Dollar' },
  { code: 'XDR', symbol: 'SDR', name: 'Special Drawing Rights' },
  { code: 'XOF', symbol: 'Fr', name: 'West African CFA Franc' },
  { code: 'XPF', symbol: 'Fr', name: 'CFP Franc' },
  { code: 'YER', symbol: '﷼', name: 'Yemeni Rial' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'ZMW', symbol: 'ZK', name: 'Zambian Kwacha' },
  { code: 'ZWL', symbol: '$', name: 'Zimbabwean Dollar' }
];

export const UNITS = [
  'pcs',
  'hrs',
  'days',
  'kg',
  'g',
  'l',
  'ml',
  'm',
  'cm',
  'ft',
  'box',
  'pack',
  'set',
  'unit',
];

export const COMMON_TAXES = [
  { label: 'SST 6%', type: 'percentage' as TaxDiscountType, value: 6 },
  { label: 'SST 8%', type: 'percentage' as TaxDiscountType, value: 8 },
  { label: 'GST 6%', type: 'percentage' as TaxDiscountType, value: 6 },
  { label: 'VAT 20%', type: 'percentage' as TaxDiscountType, value: 20 },
  { label: 'VAT 15%', type: 'percentage' as TaxDiscountType, value: 15 },
];

export type TicketStatus = 'open' | 'closed';

export interface SupportTicket {
  ticketId: string;
  userId: string;
  userEmail: string;
  title: string;
  description: string;
  screenshotUrl?: string; // base64 encoded compressed image
  status: TicketStatus;
  createdAt: Date;
  updatedAt: Date;
}

export interface TicketReply {
  replyId: string;
  senderId: string;
  senderEmail: string;
  isAdmin: boolean;
  text: string;
  createdAt: Date;
}

